export const VOICE_STATES = Object.freeze({
  IDLE: 'idle',
  STARTING: 'starting',
  LISTENING: 'listening',
  INTERRUPTION_CHECK: 'interruption_check',
  TRANSCRIBING: 'transcribing',
  THINKING: 'sending',
  SPEAKING: 'speaking',
  RECOVERING: 'recovering',
  MUTED: 'muted',
  ERROR: 'error',
});

const ALLOWED_TRANSITIONS = Object.freeze({
  idle: new Set(['starting', 'listening', 'muted', 'error']),
  listening: new Set(['interruption_check', 'transcribing', 'sending', 'thinking', 'speaking', 'muted', 'idle', 'error']),
  interruption_check: new Set(['listening', 'sending', 'thinking', 'speaking', 'recovering', 'muted', 'idle', 'error']),
  transcribing: new Set(['sending', 'thinking', 'listening', 'muted', 'idle', 'error']),
  sending: new Set(['interruption_check', 'speaking', 'recovering', 'listening', 'muted', 'idle', 'error']),
  speaking: new Set(['interruption_check', 'listening', 'sending', 'thinking', 'recovering', 'muted', 'idle', 'error']),
  muted: new Set(['listening', 'idle', 'error']),
  recovering: new Set(['listening', 'speaking', 'muted', 'idle', 'error']),
  error: new Set(['listening', 'muted', 'idle']),
  starting: new Set(['listening', 'muted', 'idle', 'error']),
  thinking: new Set(['interruption_check', 'speaking', 'recovering', 'listening', 'muted', 'idle', 'error']),
});

export function isValidVoiceTransition(currentState, nextState) {
  const current = String(currentState || 'idle');
  const next = String(nextState || 'idle');
  return current === next || Boolean(ALLOWED_TRANSITIONS[current]?.has(next));
}

export function advanceVoiceGeneration(generationRef) {
  const current = Number(generationRef?.current) || 0;
  const next = current + 1;
  if (generationRef) generationRef.current = next;
  return next;
}

export function isCurrentVoiceGeneration(generationRef, generation) {
  return Number(generationRef?.current) === Number(generation);
}

export function captureVoiceTurnOwner({
  generationId,
  voiceSessionId,
  conversationId,
  interruptionCandidateId = 0,
  sourceState = '',
  expectedStates = [],
} = {}) {
  return Object.freeze({
    generationId: Number(generationId) || 0,
    voiceSessionId: String(voiceSessionId || ''),
    conversationId: String(conversationId || ''),
    interruptionCandidateId: Number(interruptionCandidateId) || 0,
    sourceState: String(sourceState || ''),
    expectedStates: Object.freeze([...new Set(expectedStates.map((state) => String(state)))]),
  });
}

export function isCurrentVoiceTurnOwner(owner, {
  generationId,
  voiceSessionId,
  conversationId,
  interruptionCandidateId = 0,
  sourceState = '',
  state,
  expectedStates,
} = {}) {
  if (!owner) return false;
  const allowedStates = expectedStates === undefined
    ? owner.expectedStates
    : expectedStates;
  return (
    Number(owner.generationId) === Number(generationId) &&
    owner.voiceSessionId === String(voiceSessionId || '') &&
    owner.conversationId === String(conversationId || '') &&
    Number(owner.interruptionCandidateId) === Number(interruptionCandidateId || 0) &&
    (!owner.sourceState || owner.sourceState === String(sourceState || '')) &&
    (!allowedStates?.length || allowedStates.includes(String(state || '')))
  );
}

// Prefer browser-finalized recognition segments. When a browser leaves a
// stable interim tail behind after a real speech boundary, the caller may
// settle the complete stable snapshot after its bounded quiet window. This
// keeps interim captions provisional during natural pauses while preventing a
// valid final segment from being rejected forever merely because Web Speech
// retained one unfinished result slot.
export function finalizedVoiceTranscript({
  finalTranscript = '',
  stableTranscript = '',
  hasInterim = false,
  allowStableInterim = false,
} = {}) {
  if (hasInterim && !allowStableInterim) return '';
  const accepted = hasInterim && allowStableInterim
    ? (stableTranscript || finalTranscript)
    : finalTranscript;
  return String(accepted || '').replace(/\s+/g, ' ').trim();
}

export function abortVoiceTurn(controllerRef) {
  const controller = controllerRef?.current;
  if (!controller) return false;
  controllerRef.current = null;
  try {
    controller.abort();
    return true;
  } catch (_) {
    return false;
  }
}

export function beginAbortableVoiceTurn(controllerRef, generationRef) {
  abortVoiceTurn(controllerRef);
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  if (controllerRef) controllerRef.current = controller;
  const generation = advanceVoiceGeneration(generationRef);
  return { controller, generation, signal: controller?.signal };
}

export function captionForVoiceState({
  state,
  userTranscript = '',
  assistantText = '',
}) {
  const normalizedState = String(state || 'idle');
  if (normalizedState === 'speaking') return String(assistantText || '');
  if (normalizedState === 'transcribing') {
    return String(userTranscript || '');
  }
  // Once an utterance is accepted, the listening caption releases ownership
  // immediately. THINKING lasts only for real generation/TTS preparation and
  // must never keep a finished user transcript stranded on screen.
  if (normalizedState === 'sending' || normalizedState === 'thinking') return '';
  if (normalizedState === 'listening') {
    // A generated answer must remain readable if browser audio playback is
    // rejected. The next recognized user words still take ownership at once.
    return String(userTranscript || assistantText || '');
  }
  if (normalizedState === 'interruption_check') return String(userTranscript || '');
  if (normalizedState === 'muted' || normalizedState === 'error') {
    return String(userTranscript || assistantText || '');
  }
  return '';
}

/**
 * Authoritative, framework-neutral owner for voice state and caption ownership.
 * Public and developer React surfaces subscribe through onSnapshot, while all
 * transition validation and caption selection remains here.
 */
export function createVoiceSessionController({
  initialState = VOICE_STATES.IDLE,
  onInvalidTransition = null,
  onSnapshot = null,
  timers = createVoiceTimerRegistry(),
} = {}) {
  let interruptionOrigin = null;
  let snapshot = {
    state: String(initialState || VOICE_STATES.IDLE),
    userTranscript: '',
    assistantText: '',
    caption: '',
  };

  const publish = () => {
    snapshot = {
      ...snapshot,
      caption: captionForVoiceState(snapshot),
    };
    onSnapshot?.({ ...snapshot });
    return { ...snapshot };
  };

  const update = (changes = {}) => {
    snapshot = { ...snapshot, ...changes };
    return publish();
  };

  return {
    getSnapshot() {
      return { ...snapshot };
    },
    transition(nextState, changes = {}) {
      const next = String(nextState || VOICE_STATES.IDLE);
      if (!isValidVoiceTransition(snapshot.state, next)) {
        onInvalidTransition?.({ currentState: snapshot.state, nextState: next });
        return false;
      }
      update({ ...changes, state: next });
      return true;
    },
    setUserTranscript(value) {
      return update({ userTranscript: String(value || '') });
    },
    setAssistantText(value) {
      return update({ assistantText: String(value || '') });
    },
    setCaptions({ userTranscript, assistantText } = {}) {
      const changes = {};
      if (userTranscript !== undefined) changes.userTranscript = String(userTranscript || '');
      if (assistantText !== undefined) changes.assistantText = String(assistantText || '');
      return update(changes);
    },
    beginInterruption() {
      if (snapshot.state !== VOICE_STATES.INTERRUPTION_CHECK) {
        interruptionOrigin = snapshot.state;
      }
      return this.transition(VOICE_STATES.INTERRUPTION_CHECK);
    },
    confirmInterruption(userTranscript = '') {
      interruptionOrigin = null;
      return this.transition(VOICE_STATES.LISTENING, {
        userTranscript: String(userTranscript || ''),
      });
    },
    rejectInterruption() {
      const origin = interruptionOrigin;
      interruptionOrigin = null;
      if (!origin) return false;
      return this.transition(origin, { userTranscript: '' });
    },
    beginThinking() {
      return this.transition(VOICE_STATES.THINKING, { userTranscript: '' });
    },
    resumeSpeaking(assistantText) {
      const changes = { userTranscript: '' };
      if (assistantText !== undefined) changes.assistantText = String(assistantText || '');
      return this.transition(VOICE_STATES.SPEAKING, changes);
    },
    reset(nextState = VOICE_STATES.IDLE) {
      timers.cancelAll();
      interruptionOrigin = null;
      snapshot = {
        state: String(nextState || VOICE_STATES.IDLE),
        userTranscript: '',
        assistantText: '',
        caption: '',
      };
      return publish();
    },
    timers,
    teardown() {
      timers.cancelAll();
    },
  };
}

export function advanceVoicePlaybackOffset({
  offset = 0,
  startedAt = 0,
  currentTime = 0,
  duration = Number.POSITIVE_INFINITY,
} = {}) {
  const numericOffset = Number(offset);
  const numericStartedAt = Number(startedAt);
  const numericCurrentTime = Number(currentTime);
  const safeOffset = Number.isFinite(numericOffset) ? Math.max(0, numericOffset) : 0;
  const safeStartedAt = Number.isFinite(numericStartedAt) ? numericStartedAt : 0;
  const safeCurrentTime = Number.isFinite(numericCurrentTime) ? numericCurrentTime : safeStartedAt;
  const elapsed = Math.max(0, safeCurrentTime - safeStartedAt);
  const next = safeOffset + elapsed;
  const safeDuration = Number(duration);
  return Number.isFinite(safeDuration) ? Math.min(Math.max(0, safeDuration), next) : next;
}

export function createVoiceTimerRegistry({
  clearTimer = clearTimeout,
  scheduleTimer = setTimeout,
} = {}) {
  const timers = new Map();
  return {
    schedule(name, callback, delayMs) {
      if (timers.has(name)) clearTimer(timers.get(name));
      const timer = scheduleTimer(() => {
        timers.delete(name);
        callback();
      }, Math.max(0, Number(delayMs) || 0));
      timers.set(name, timer);
      return timer;
    },
    cancel(name) {
      if (!timers.has(name)) return false;
      clearTimer(timers.get(name));
      timers.delete(name);
      return true;
    },
    cancelAll() {
      for (const timer of timers.values()) clearTimer(timer);
      timers.clear();
    },
    has(name) {
      return timers.has(name);
    },
  };
}

export function createVoiceProgressWatchdog({
  timeoutMs = 2500,
  scheduleTimer = (callback, delay) => setTimeout(callback, delay),
  clearTimer = (timer) => clearTimeout(timer),
  onTimeout = () => {},
} = {}) {
  let timer = null;
  let owner = null;

  const clear = () => {
    if (timer !== null) clearTimer(timer);
    timer = null;
    owner = null;
  };

  return Object.freeze({
    arm(nextOwner, isCurrent = () => true) {
      clear();
      owner = Object.freeze({ ...(nextOwner || {}) });
      const armedOwner = owner;
      timer = scheduleTimer(() => {
        timer = null;
        owner = null;
        if (isCurrent(armedOwner)) onTimeout(armedOwner);
      }, Math.max(250, Number(timeoutMs) || 2500));
      return armedOwner;
    },
    clear,
    isArmed() {
      return timer !== null;
    },
  });
}

/**
 * Framework-neutral absolute deadline for one detected user utterance.
 * start() is idempotent so interim-result storms cannot postpone the ceiling.
 * Normal speechend/final-result paths call finish(); teardown calls cancel().
 */
export function createVoiceUtteranceGuard({
  timers = createVoiceTimerRegistry(),
  maximumMs = 60000,
  onMaximum = null,
  timerName = 'maximum-utterance',
} = {}) {
  const numericMaximum = Number(maximumMs);
  const boundedMaximum = Number.isFinite(numericMaximum)
    ? Math.min(120000, Math.max(1000, numericMaximum))
    : 60000;
  let active = false;

  return Object.freeze({
    start() {
      if (active) return boundedMaximum;
      active = true;
      timers.schedule(timerName, () => {
        if (!active) return;
        active = false;
        onMaximum?.();
      }, boundedMaximum);
      return boundedMaximum;
    },
    finish() {
      const wasActive = active;
      active = false;
      timers.cancel(timerName);
      return wasActive;
    },
    cancel() {
      active = false;
      return timers.cancel(timerName);
    },
    isActive() {
      return active;
    },
    maximumMs: boundedMaximum,
  });
}
